import type { RivalDto, RivalSectorDto } from '@rw/shared';
import { Link, useNavigate, useParams } from 'react-router';
import { Page } from '../../components/Layout';
import { EmptyState, QueryView } from '../../components/states';
import { Avatar, Button, Card, SectionTitle } from '../../components/ui';
import { useRival, useRivals } from '../../hooks/queries';
import { timeAgo } from '../../lib/format';
import { useT } from '../../i18n';

const PREVIEW = 5;

/** Map link that marks these sectors red (and optionally opens one). */
function lostOnMapUrl(keys: string[], focus?: string) {
  const q = new URLSearchParams({ lost: keys.join(',') });
  if (focus) q.set('focus', focus);
  return `/map?${q}`;
}

/** Profile section: the runners you trade the most sectors with. */
export function RivalsCard() {
  const q = useRivals();
  const t = useT();
  return (
    <QueryView
      query={q}
      empty={(list) =>
        list.length ? null : (
          <Card className="text-sm text-ink-400">
            {t('No rivals yet. Take a sector someone else holds — or lose one — and they show up here.')}
          </Card>
        )
      }
    >
      {(list) => (
        <div data-testid="rivals-card">
          <RivalList rivals={list.slice(0, PREVIEW)} />
          {list.length > PREVIEW ? (
            <Link to="/rivals" className="mt-2 block text-center text-sm font-semibold text-ink-300">
              {t('See all {n} rivals', { n: list.length })}
            </Link>
          ) : null}
        </div>
      )}
    </QueryView>
  );
}

export function RivalsPage() {
  const q = useRivals();
  const navigate = useNavigate();
  const t = useT();
  return (
    <Page>
      <BackButton onClick={() => navigate(-1)} />
      <h1 className="font-display text-3xl font-bold">{t('Rivals')}</h1>
      <p className="mt-1 text-sm text-ink-400">{t('Sectors you took from each runner, and they took from you.')}</p>
      <div className="mt-4">
        <QueryView query={q} empty={(l) => (l.length ? null : <EmptyState icon="⚔️" title={t('No rivals yet')} />)}>
          {(list) => <RivalList rivals={list} />}
        </QueryView>
      </div>
    </Page>
  );
}

function RivalList({ rivals }: { rivals: RivalDto[] }) {
  const t = useT();
  return (
    <ul className="space-y-2">
      {rivals.map((r) => (
        <li key={r.user.id}>
          <Link to={`/rivals/${r.user.id}`} className="flex items-center gap-3 rounded-2xl bg-ink-850 px-4 py-3" data-testid="rival-row">
            <Avatar name={r.user.displayName} color={r.user.color} src={r.user.avatarUrl} size="sm" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold">{r.user.displayName}</span>
              <span className="block text-xs text-ink-400">
                {t('You took {a} · They took {b}', { a: r.took, b: r.lost })}
              </span>
            </span>
            {r.theyHold ? (
              <span className="shrink-0 rounded-full bg-enemy/15 px-2 py-0.5 text-xs font-bold text-enemy">
                {t('{n} to retake', { n: r.theyHold })}
              </span>
            ) : null}
            <span className="text-ink-500">›</span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

export function RivalPage() {
  const { id } = useParams<{ id: string }>();
  const q = useRival(id!);
  const navigate = useNavigate();
  const t = useT();
  return (
    <Page>
      <BackButton onClick={() => navigate(-1)} />
      <QueryView query={q}>
        {(r) => (
          <div data-testid="rival-detail">
            <Link to={`/players/${r.user.id}`} className="flex items-center gap-3">
              <Avatar name={r.user.displayName} color={r.user.color} src={r.user.avatarUrl} size="lg" />
              <span className="min-w-0">
                <span className="block text-xs font-semibold uppercase tracking-wider text-ink-400">{t('You vs')}</span>
                <span className="block truncate font-display text-2xl font-bold">{r.user.displayName}</span>
              </span>
            </Link>
            <div className="mt-5 grid grid-cols-2 gap-3 text-center">
              <Card>
                <div className="font-display text-4xl font-extrabold text-volt tabular" data-testid="rival-took">{r.took}</div>
                <div className="mt-1 text-xs text-ink-400">{t('Sectors you took')}</div>
              </Card>
              <Card>
                <div className="font-display text-4xl font-extrabold text-enemy tabular" data-testid="rival-lost">{r.lost}</div>
                <div className="mt-1 text-xs text-ink-400">{t('Sectors they took')}</div>
              </Card>
            </div>

            <SectionTitle
              action={
                r.theyHoldSectors.length ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => navigate(lostOnMapUrl(r.theyHoldSectors.map((s) => s.gridKey)))}
                    data-testid="rival-show-lost"
                  >
                    {t('Show on map')}
                  </Button>
                ) : null
              }
            >
              {t('To take back · {n}', { n: r.theyHold })}
            </SectionTitle>
            {r.theyHoldSectors.length ? (
              <SectorList sectors={r.theyHoldSectors} lost all={r.theyHoldSectors.map((s) => s.gridKey)} />
            ) : (
              <p className="text-sm text-ink-400">{t('{name} holds none of your sectors right now.', { name: r.user.displayName })}</p>
            )}

            <SectionTitle>{t('Yours, taken from them · {n}', { n: r.youHold })}</SectionTitle>
            {r.youHoldSectors.length ? (
              <SectorList sectors={r.youHoldSectors} />
            ) : (
              <p className="text-sm text-ink-400">{t('You hold none of their sectors right now.')}</p>
            )}
          </div>
        )}
      </QueryView>
    </Page>
  );
}

function SectorList({ sectors, lost = false, all }: { sectors: RivalSectorDto[]; lost?: boolean; all?: string[] }) {
  const t = useT();
  return (
    <ul className="divide-y divide-ink-800 overflow-hidden rounded-2xl bg-ink-850" data-testid={lost ? 'rival-lost-sectors' : 'rival-held-sectors'}>
      {sectors.map((s) => (
        <li key={s.territoryId} className="flex items-center gap-3 px-4 py-2.5 text-sm">
          <Link to={lost ? lostOnMapUrl(all ?? [s.gridKey], s.gridKey) : `/map?focus=${encodeURIComponent(s.gridKey)}`} className="min-w-0 flex-1">
            <span className={`font-semibold ${lost ? 'text-enemy' : 'text-volt'}`}>⬡ {s.gridKey}</span>
            <span className="ml-2 text-xs text-ink-400">{timeAgo(s.capturedAt)}</span>
          </Link>
          <Link to={`/runs/${s.runId}`} className="shrink-0 text-xs font-semibold text-ink-300">
            {t('Run')} ›
          </Link>
        </li>
      ))}
    </ul>
  );
}

function BackButton({ onClick }: { onClick: () => void }) {
  const t = useT();
  return (
    <button onClick={onClick} className="mb-2 text-sm font-semibold text-ink-300">
      {t('← Back')}
    </button>
  );
}
